import React, { useMemo, useRef, useState } from "react";
import { Formik, Form } from "formik";
import { Link, useSearchParams } from "react-router-dom";
import { Edit, Visibility } from "@mui/icons-material";
import { useQueryClient } from "@tanstack/react-query";

import ViewSchoolDetails from "components/ViewSchoolDetails/ViewSchoolDetails";
import SchoolForm from "components/SchoolForm/SchoolForm";
import {
  useFetchSchoolDetails,
  useEditSchoolDetails,
  useFetchSchoolCategoryDetails,
} from "store/backend/apiHooks";
import {
  buildSchoolStatusUpdatePayload,
  buildSchoolUpdatePayload,
  canEditSchool,
  createSchoolEditSchema,
  getSchoolApiErrorMessage,
  getSchoolAgentUserIds,
  isEditUdiseCodeLocked,
  useSchoolDetailsLookups,
} from "helpers/schoolUtils";
import Loader from "shared/Icons/Loader/Loader";
import { Modal, PrimaryButton, SecondaryButton } from "widgets";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";
import {
  SCHOOL_FEE_CATEGORY_MISSING_WARNING,
  SCHOOL_ID_QUERY_PARAM,
  SCHOOL_QUERY_KEYS,
  SCHOOL_STATUS,
} from "constants/schoolConstants";
import routes from "constants/routes";
import editStyles from "../../components/EditTenantDetails/EditTenantDetails.module.scss";
import tenantPageStyles from "../TenantDetails/TenantDetails.module.scss";
import styles from "./SchoolDetails.module.scss";

const invalidateSchoolDetails = (queryClient, schoolId) =>
  queryClient.invalidateQueries({
    queryKey: [SCHOOL_QUERY_KEYS.DETAILS, schoolId],
  });

const invalidateSchoolCategoryDetails = (queryClient, schoolCode) => {
  if (!schoolCode) return;
  queryClient.invalidateQueries({
    queryKey: [SCHOOL_QUERY_KEYS.CATEGORY_DETAILS, schoolCode],
  });
};

const warnIfFeeAndCategoryUnavailable = ({
  schoolCode,
  isFetched,
  isPending,
  isError,
  categoryDetails,
  warnedSchoolCodeRef,
  openSnackbar,
}) => {
  const isUnavailable = isError || categoryDetails == null;
  if (!schoolCode || !isFetched || isPending || !isUnavailable) return;
  if (warnedSchoolCodeRef.current === schoolCode) return;
  warnedSchoolCodeRef.current = schoolCode;
  queueMicrotask(() => {
    openSnackbar(SCHOOL_FEE_CATEGORY_MISSING_WARNING, "warning");
  });
};

const SchoolEditForm = ({
  schoolDetails,
  schoolId,
  onViewDetails,
  editSchoolDetails,
  isEditSchoolDetailsLoading,
  annualSchoolFee,
  isLoadingFeeAndCategory,
}) => {
  const { openSnackbar } = useSnackbarContext();
  const queryClient = useQueryClient();
  const mapping = useSchoolDetailsLookups(schoolDetails, {
    includeIds: true,
    annualSchoolFee,
  });
  const { initialValues, isFormReady, countryOptions } = mapping;
  const assignedAgentUserIds = useMemo(
    () => getSchoolAgentUserIds(schoolDetails),
    [schoolDetails],
  );
  if (!schoolDetails || !schoolId) return null;
  if (!isFormReady || !initialValues || isLoadingFeeAndCategory) {
    return (
      <div className={tenantPageStyles.loadingWrap} role="status" aria-live="polite">
        <Loader inline />
      </div>
    );
  }
  return (
    <div className={editStyles.EditTenantDetailsContainer}>
      <div className={editStyles.tenantDetailsContent}>
        <Formik
          key={schoolId}
          initialValues={initialValues}
          validationSchema={createSchoolEditSchema(countryOptions)}
          validateOnMount={false}
          validateOnChange
          validateOnBlur
          onSubmit={(values, { setSubmitting }) => {
            setSubmitting(true);
            editSchoolDetails(
              {
                schoolId,
                payload: buildSchoolUpdatePayload(
                  values,
                  schoolDetails,
                  countryOptions,
                ),
              },
              {
                onSuccess: () => {
                  setSubmitting(false);
                  invalidateSchoolDetails(queryClient, schoolId);
                  invalidateSchoolCategoryDetails(
                    queryClient,
                    schoolDetails.schoolCode,
                  );
                  openSnackbar("School updated successfully!", "success");
                  onViewDetails();
                },
                onError: (error) => {
                  setSubmitting(false);
                  openSnackbar(
                    `Error updating school: ${getSchoolApiErrorMessage(error)}`,
                    "error",
                  );
                },
              },
            );
          }}
        >
          {({
            values,
            errors,
            touched,
            setFieldValue,
            setFieldTouched,
            setFieldError,
            handleBlur,
            handleSubmit,
            isSubmitting,
            submitCount,
          }) => (
            <Form onSubmit={handleSubmit}>
              <SchoolForm
                mapping={mapping}
                values={values}
                touched={touched}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                setFieldError={setFieldError}
                handleBlur={handleBlur}
                errors={errors}
                submitCount={submitCount}
                udiseCodeDisabled={isEditUdiseCodeLocked(schoolDetails, values)}
                assignedAgentUserIds={assignedAgentUserIds}
              />
              <div className={editStyles.submitRow}>
                <PrimaryButton
                  type="submit"
                  disabled={isSubmitting || isEditSchoolDetailsLoading}
                >
                  {isSubmitting || isEditSchoolDetailsLoading ? (
                    <Loader inline />
                  ) : (
                    "Submit"
                  )}
                </PrimaryButton>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

const SchoolDetails = () => {
  const [searchParams] = useSearchParams();
  const schoolId = searchParams.get(SCHOOL_ID_QUERY_PARAM);
  const [editSchool, setEditSchool] = useState(false);
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbarContext();
  const { mutate: editSchoolDetails, isPending: isEditSchoolDetailsLoading } =
    useEditSchoolDetails();
  const {
    data: schoolResponse,
    isPending: isFetchSchoolDetailsLoading,
    isError,
    error,
  } = useFetchSchoolDetails(schoolId);
  const schoolDetails = schoolResponse?.data;
  const schoolCode = schoolDetails?.schoolCode;
  const warnedFeeCategorySchoolCodeRef = useRef(null);
  const {
    data: schoolCategoryResponse,
    isPending: isSchoolCategoryPending,
    isError: isSchoolCategoryError,
    isFetched: isSchoolCategoryFetched,
  } = useFetchSchoolCategoryDetails(schoolCode);
  const schoolCategoryDetails = schoolCategoryResponse?.data ?? null;
  const fees = schoolCategoryDetails?.fees ?? null;
  const category = schoolCategoryDetails?.category ?? null;
  const isLoadingFeeAndCategory = Boolean(schoolCode) && isSchoolCategoryPending;
  const hasEditAccess = canEditSchool();
  const currentStatusId = schoolDetails?.status ?? SCHOOL_STATUS.ACTIVE;
  const isSchoolActive = currentStatusId === SCHOOL_STATUS.ACTIVE;

  warnIfFeeAndCategoryUnavailable({
    schoolCode,
    isFetched: isSchoolCategoryFetched,
    isPending: isSchoolCategoryPending,
    isError: isSchoolCategoryError,
    categoryDetails: schoolCategoryDetails,
    warnedSchoolCodeRef: warnedFeeCategorySchoolCodeRef,
    openSnackbar,
  });

  if (isFetchSchoolDetailsLoading) {
    return (
      <div className={styles.mainContainer}>
        <div className={styles.loadingWrap} role="status" aria-live="polite" aria-busy="true">
          <Loader inline />
        </div>
      </div>
    );
  }
  if (isError) {
    return (
      <div className={styles.mainContainer}>
        <div className={styles.toolbar}>
          <Link className={styles.backLink} to={routes.schools.schools}>
            ← Back to schools
          </Link>
        </div>
        <div className={styles.errorMessage}>
          {getSchoolApiErrorMessage(error, "Unable to load school details.")}
        </div>
      </div>
    );
  }
  const handleToggleSchoolStatus = () => {
    if (!schoolId) return;
    if (!schoolDetails?.countryId) {
      openSnackbar(
        "Unable to update school status: country is required.",
        "error",
      );
      return;
    }
    editSchoolDetails(
      {
        schoolId,
        payload: buildSchoolStatusUpdatePayload(
          schoolDetails.countryId,
          isSchoolActive ? SCHOOL_STATUS.INACTIVE : SCHOOL_STATUS.ACTIVE,
        ),
      },
      {
        onSuccess: () => {
          setStatusConfirmOpen(false);
          if (isSchoolActive) setEditSchool(false);
          invalidateSchoolDetails(queryClient, schoolId);
          openSnackbar(
            `School ${isSchoolActive ? "deactivated" : "activated"} successfully!`,
            "success",
          );
        },
        onError: (updateError) => {
          openSnackbar(
            `Error updating school status: ${getSchoolApiErrorMessage(updateError)}`,
            "error",
          );
        },
      },
    );
  };
  const schoolName = schoolDetails?.schoolName || "this school";
  return (
    <div className={styles.mainContainer}>
      <div className={styles.toolbar}>
        <Link className={styles.backLink} to={routes.schools.schools}>
          ← Back to schools
        </Link>
        <h1 className={styles.pageTitle}>
          {editSchool ? "Edit school details" : "School details"}
        </h1>
        {hasEditAccess ? (
          <div className={styles.toolbarActions}>
            <SecondaryButton
              disabled={isEditSchoolDetailsLoading || !schoolDetails || editSchool}
              onClick={() => setStatusConfirmOpen(true)}
            >
              {isSchoolActive ? "Deactivate school" : "Activate school"}
            </SecondaryButton>
            {isSchoolActive ? (
              <PrimaryButton
                className={styles.editButton}
                onClick={() => setEditSchool(!editSchool)}
              >
                {editSchool ? "View school" : "Edit school"}
                {editSchool ? (
                  <Visibility className={styles.icon} />
                ) : (
                  <Edit className={styles.icon} />
                )}
              </PrimaryButton>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className={styles.schoolDetailsContent}>
        {editSchool && isSchoolActive ? (
          <SchoolEditForm
            schoolDetails={schoolDetails}
            schoolId={schoolId}
            onViewDetails={() => setEditSchool(false)}
            editSchoolDetails={editSchoolDetails}
            isEditSchoolDetailsLoading={isEditSchoolDetailsLoading}
            annualSchoolFee={fees}
            isLoadingFeeAndCategory={isLoadingFeeAndCategory}
          />
        ) : (
          <ViewSchoolDetails
            schoolDetails={schoolDetails}
            fees={fees}
            category={category}
            isLoadingFeeAndCategory={isLoadingFeeAndCategory}
          />
        )}
      </div>
      <Modal
        open={statusConfirmOpen}
        onClose={() => {
          if (!isEditSchoolDetailsLoading) setStatusConfirmOpen(false);
        }}
        title={isSchoolActive ? "Deactivate school?" : "Activate school?"}
        actionLabel={isSchoolActive ? "Deactivate" : "Activate"}
        onAction={handleToggleSchoolStatus}
      >
        <p>
          Are you sure you want to {isSchoolActive ? "deactivate" : "activate"}{" "}
          {schoolName}?
        </p>
      </Modal>
    </div>
  );
};

export default SchoolDetails;
